// src/pages/Institutional/PrivacyPolicy.js
import React from 'react'
import { Box, Typography, Paper, Chip } from '@mui/material'
import Secao, { Leitura } from '../../components/Layout/Secao'
import {
  Security,
  VerifiedUser,
  Lock,
  Share,
  Storage,
  Cookie,
  Delete,
  ContactMail
} from '@mui/icons-material'

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  })

  const userProvidedData = [
    'Nome',
    'E-mail',
    'Telefone',
    'Senha',
    'Tipo de conta (proprietário, corretor ou imobiliária)',
    'CRECI (para corretores)',
    'CNPJ e razão social (para imobiliárias)',
    'Informações sobre os imóveis anunciados'
  ]

  const generatedData = [
    'Pesquisas realizadas',
    'Páginas visitadas',
    'Cliques em imóveis',
    'Leads enviados',
    'Dados de navegação e performance'
  ]

  const dataUsage = [
    'Exibir anúncios relevantes',
    'Facilitar contato entre comprador e anunciante',
    'Melhorar filtros, recomendações e ranking',
    'Garantir segurança e prevenir fraudes',
    'Enviar avisos importantes ao usuário',
    'Aprimorar a experiência na plataforma'
  ]

  const securityMeasures = [
    'Criptografia em tráfego (HTTPS)',
    'Armazenamento seguro',
    'Controle de acesso',
    'Prevenção contra conteúdo duplicado e fraudulento',
    'Validação de CRECI',
    'Monitoramento básico contra abuso'
  ]

  const userRights = [
    'Acesso aos seus dados',
    'Correção de informações',
    'Exclusão da conta',
    'Revogação de consentimento',
    'Portabilidade dos dados',
    'Esclarecimentos sobre o uso de dados'
  ]

  return (
    // Mesmo trilho do cabecalho e do rodape; a largura de leitura de
    // 768px fica dentro dele, encostada na esquerda. Era Container md
    // centralizado: o texto comecava 256px a direita do logo acima dele.
    <Secao fundo='padrao' ritmo='solto' sx={{ minHeight: '100vh' }}>
      <Leitura>
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Security sx={{ fontSize: '4rem', color: 'primary.main', mb: 2 }} />
          <Typography
            variant='h3'
            sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
          >
            Política de Privacidade e Segurança
          </Typography>
          <Chip
            label={`Última atualização: ${currentDate}`}
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Introdução */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 4, bgcolor: 'primary.light', borderRadius: 3 }}
        >
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.primary', mb: 2 }}
          >
            A privacidade dos usuários é prioridade para o{' '}
            <strong>umarkt</strong>.
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.primary', mb: 2 }}
          >
            Esta política explica como coletamos, usamos, armazenamos e
            protegemos seus dados ao utilizar nossa plataforma.
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.primary' }}
          >
            Ao acessar o site ou utilizar qualquer recurso, você concorda com as
            práticas descritas abaixo, em conformidade com a{' '}
            <strong>
              LGPD (Lei Geral de Proteção de Dados – Lei 13.709/2018)
            </strong>
            .
          </Typography>
        </Paper>

        {/* 1. Quais dados coletamos */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            1. Quais dados coletamos
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            Coletamos apenas informações necessárias para o funcionamento do
            marketplace.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
          >
            1.1. Dados fornecidos pelo usuário
          </Typography>
          <Box sx={{ mb: 3 }}>
            {userProvidedData.map((item, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main'
                  }}
                />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
          >
            1.2. Dados gerados ao usar o portal
          </Typography>
          <Box sx={{ mb: 3 }}>
            {generatedData.map((item, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main'
                  }}
                />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            1.3. Dados de contato e comunicação
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Quando um usuário envia mensagem pelo WhatsApp ou formulário,
            armazenamos informações relacionadas ao lead.
          </Typography>
        </Paper>

        {/* 2. Como usamos os dados */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            2. Como usamos os dados
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
          >
            Utilizamos dados para:
          </Typography>
          <Box>
            {dataUsage.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <VerifiedUser
                  sx={{ color: 'success.main', fontSize: '1.2rem' }}
                />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              mt: 3,
              p: 3,
              bgcolor: 'success.light',
              borderRadius: 2,
              borderLeft: '4px solid',
              borderColor: 'success.main'
            }}
          >
            <Typography
              variant='body1'
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              A umarkt não vende, não aluga e não negocia dados com terceiros.
            </Typography>
          </Box>
        </Paper>

        {/* 3. Compartilhamento de dados */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            3. Compartilhamento de dados
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            Dados são compartilhados apenas em situações específicas e
            necessárias:
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Share sx={{ color: 'primary.main' }} />
              <Box>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                >
                  3.1. Para anunciantes receberem leads
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Quando um interessado envia um contato, seus dados são
                  compartilhados com o anunciante responsável.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Storage sx={{ color: 'info.main' }} />
              <Box>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                >
                  3.2. Com prestadores de serviço essenciais
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 1 }}
                >
                  Exemplo:
                </Typography>
                <Box component='ul' sx={{ pl: 2, m: 0 }}>
                  <Typography
                    component='li'
                    variant='body2'
                    sx={{ color: 'text.secondary', mb: 0.5 }}
                  >
                    Serviços de hospedagem
                  </Typography>
                  <Typography
                    component='li'
                    variant='body2'
                    sx={{ color: 'text.secondary', mb: 0.5 }}
                  >
                    Provedores de e-mail
                  </Typography>
                  <Typography
                    component='li'
                    variant='body2'
                    sx={{ color: 'text.secondary' }}
                  >
                    Integrações técnicas de segurança
                  </Typography>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', mt: 1 }}
                >
                  Todos seguem padrões de proteção compatíveis com a LGPD.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <VerifiedUser sx={{ color: 'warning.main' }} />
              <Box>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                >
                  3.3. Para cumprimento de obrigações legais
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Apenas quando houver solicitação das autoridades competentes.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* 4. Segurança e Proteção */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            4. Segurança e Proteção
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            A umarkt adota práticas modernas de proteção:
          </Typography>
          <Box sx={{ mb: 3 }}>
            {securityMeasures.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <Lock sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: 'info.light',
              borderRadius: 2
            }}
          >
            <Typography
              variant='body2'
              sx={{ color: 'text.primary', fontStyle: 'italic' }}
            >
              Nenhum sistema é 100% imune a riscos, mas adotamos medidas
              razoáveis para garantir segurança contínua.
            </Typography>
          </Box>
        </Paper>

        {/* 5. Direitos do Usuário (LGPD) */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            5. Direitos do Usuário (LGPD)
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            Você pode solicitar, a qualquer momento:
          </Typography>
          <Box>
            {userRights.map((item, index) => (
              <Chip
                key={index}
                label={item}
                sx={{
                  m: 0.5,
                  fontWeight: 500,
                  bgcolor: 'grey.100'
                }}
              />
            ))}
          </Box>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 3 }}>
            Para exercer seus direitos, basta entrar em contato pelos canais ao
            final da página.
          </Typography>
        </Paper>

        {/* 6. Cookies */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Box
            sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}
          >
            <Cookie sx={{ color: 'primary.main', fontSize: '2rem' }} />
            <Box>
              <Typography
                variant='h5'
                sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
              >
                6. Cookies e Tecnologias de Navegação
              </Typography>
              <Typography
                variant='body1'
                sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
              >
                Utilizamos cookies para melhorar:
              </Typography>
              <Box component='ul' sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Experiência do usuário
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Velocidade do site
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Personalização de resultados
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary' }}
                >
                  Funcionamento dos filtros
                </Typography>
              </Box>
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
              >
                Cookies podem ser bloqueados no navegador, mas isso pode
                prejudicar algumas funcionalidades.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 7. Retenção e Exclusão */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Box
            sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}
          >
            <Delete sx={{ color: 'error.main', fontSize: '2rem' }} />
            <Box>
              <Typography
                variant='h5'
                sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
              >
                7. Retenção e Exclusão de Dados
              </Typography>
              <Typography
                variant='body1'
                sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
              >
                Mantemos dados pessoais apenas pelo tempo necessário para:
              </Typography>
              <Box component='ul' sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Manter a conta ativa
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Cumprir obrigações legais
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary' }}
                >
                  Garantir funcionamento correto do portal
                </Typography>
              </Box>
              <Typography
                variant='body1'
                sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
              >
                O usuário pode solicitar exclusão a qualquer momento. Ao excluir
                a conta:
              </Typography>
              <Box component='ul' sx={{ pl: 3 }}>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Dados pessoais são removidos
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Anúncios ativos são encerrados
                </Typography>
                <Typography
                  component='li'
                  variant='body2'
                  sx={{ color: 'text.secondary' }}
                >
                  Leads armazenados são anonimizados
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* 8. Alterações nesta Política */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            8. Alterações nesta Política
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Esta política pode ser atualizada para melhorar clareza, segurança e
            transparência. Sempre exibiremos a data da última atualização.
          </Typography>
        </Paper>

        {/* 9. Contato e Solicitações */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <ContactMail sx={{ fontSize: '3rem', mb: 2 }} />
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 2 }}>
            9. Contato e Solicitações da LGPD
          </Typography>
          <Typography variant='body1' sx={{ mb: 2, opacity: 0.9 }}>
            Para dúvidas, solicitações ou direitos relacionados à LGPD:
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>E-mail:</strong> contato@umarkt.com.br
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>WhatsApp:</strong> (11) 9 8139-6930
          </Typography>
          <Typography variant='body2'>
            <strong>Site:</strong> www.umarkt.com.br
          </Typography>
        </Paper>
      </Leitura>
    </Secao>
  )
}

export default PrivacyPolicy
